import React, { useState } from 'react';
import { 
  BarChart, 
  PieChart, 
  TrendingUp, 
  Download, 
  Filter,
  DollarSign,
  Users,
  MousePointerClick,
  Activity,
  Calendar,
  LayoutDashboard,
  Target,
  Megaphone,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { ClientEntity } from '../types';

export const ReportsDashboard: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const [activeTab, setActiveTab] = useState<'executiva' | 'campanhas' | 'funil' | 'exportacao'>('executiva');
  const [dateRange, setDateRange] = useState('ultimos-30');

  const tabs = [
    { id: 'executiva', label: 'Visão Executiva', icon: LayoutDashboard },
    { id: 'campanhas', label: 'Campanhas & Criativos', icon: Megaphone },
    { id: 'funil', label: 'Funil & Atendimento', icon: Filter },
    { id: 'exportacao', label: 'Exportar Relatório', icon: Download },
  ] as const;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 flex flex-col h-full">
      <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
            <BarChart size={20} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Relatórios & Performance</h2>
            <p className="text-xs text-gray-500">Métricas padronizadas de aquisição e conversão</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg text-sm">
            <Calendar size={14} className="text-gray-400" />
            <select 
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-transparent border-none text-gray-700 font-medium focus:ring-0 cursor-pointer outline-none"
            >
              <option value="ultimos-7">Últimos 7 dias</option>
              <option value="ultimos-15">Últimos 15 dias</option>
              <option value="ultimos-30">Últimos 30 dias</option>
              <option value="este-mes">Este mês</option>
              <option value="mes-passado">Mês passado</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex overflow-x-auto gap-2 pb-2 hide-scrollbar">
        {tabs.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === item.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <item.icon size={16} className={activeTab === item.id ? 'text-white' : 'text-gray-400'} />
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex-1 w-full bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6 overflow-y-auto">
         {activeTab === 'executiva' && (
           <div className="space-y-6 animate-in fade-in">
             <div className="flex items-center justify-between mb-4">
               <div>
                  <h3 className="text-lg font-bold text-gray-900">Visão Executiva</h3>
                  <p className="text-sm text-gray-500">O que você precisa saber sobre o negócio neste período.</p>
               </div>
             </div>

             {/* KPIs Principais */}
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Leads Gerados', value: '142', trend: '+12%', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'CPL (Custo por Lead)', value: 'R$ 8,40', trend: '-5%', icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                  { label: 'CPA (Custo por Aquisição)', value: 'R$ 85,00', trend: '+2%', icon: TrendingUp, color: 'text-orange-600', bg: 'bg-orange-50' },
                  { label: 'ROI Estimado', value: '3.2x', trend: '+15%', icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' }
                ].map((kpi, idx) => (
                  <div key={idx} className="border border-gray-100 bg-gray-50/50 p-4 rounded-xl">
                    <div className="flex justify-between items-start mb-3">
                      <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                        <kpi.icon size={18} />
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded border ${kpi.trend.startsWith('+') ? 'text-emerald-700 bg-emerald-50 border-emerald-100' : 'text-rose-700 bg-rose-50 border-rose-100'}`}>
                        {kpi.trend}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">{kpi.label}</p>
                      <h4 className="text-2xl font-black text-gray-900">{kpi.value}</h4>
                    </div>
                  </div>
                ))}
             </div>

             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div className="border border-gray-200 rounded-xl p-5">
                   <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><Activity size={16} className="text-gray-400"/> Resumo do Período</h4>
                   <ul className="space-y-4">
                     <li className="flex gap-3">
                        <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-600"><strong className="text-gray-900">Conversão de Agendamentos:</strong> Aumentou em 12% após a introdução das novas regras de distribuição na equipe.</p>
                     </li>
                     <li className="flex gap-3">
                        <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-600"><strong className="text-gray-900">Atenção ao CPL:</strong> A campanha de remarketing no Meta Ads apresentou aumento de 15% no custo por lead na última semana.</p>
                     </li>
                   </ul>
                </div>
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-900 text-white flex flex-col justify-center">
                   <h4 className="text-sm font-medium text-gray-400 mb-2">Ação Recomendada (Estratégia)</h4>
                   <p className="text-lg font-bold leading-relaxed mb-4">
                     Escalar a campanha de <span className="text-indigo-400">Expansão de Público (Meta)</span> e pausar os <span className="text-rose-400">Criativos C e D</span> que estão com CTR baixo.
                   </p>
                   <button className="self-start px-4 py-2 bg-white text-black text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors">
                     Aprovar Ajustes
                   </button>
                </div>
             </div>
           </div>
         )}

         {activeTab === 'campanhas' && (
           <div className="space-y-6 animate-in fade-in">
              <div className="flex items-center justify-between mb-4">
               <div>
                  <h3 className="text-lg font-bold text-gray-900">Campanhas & Criativos</h3>
                  <p className="text-sm text-gray-500">Estrutura padrão de aquisição e performance dos anúncios.</p>
               </div>
             </div>

             <div className="space-y-4">
               {/* Meta Ads Group */}
               <div className="border border-gray-200 rounded-xl overflow-hidden">
                 <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                   <div className="flex items-center gap-2">
                     <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded flex items-center justify-center font-bold text-lg">M</div>
                     <span className="font-bold text-gray-900">Meta Ads</span>
                   </div>
                   <span className="text-sm font-medium text-gray-500">3 Campanhas Ativas</span>
                 </div>
                 <div className="divide-y divide-gray-100">
                    {[
                      { name: '1. Teste de Criativos (Dor/Desejo)', status: 'Ativo', spend: 'R$ 450', leads: 42, cpl: 'R$ 10,71', roas: '1.5x', tag: 'Top Funil' },
                      { name: '2. Escala (Públicos Vencedores)', status: 'Ativo', spend: 'R$ 1.200', leads: 156, cpl: 'R$ 7,69', roas: '3.8x', tag: 'Escala' },
                      { name: '3. Remarketing (Urgência)', status: 'Ativo', spend: 'R$ 300', leads: 45, cpl: 'R$ 6,66', roas: '4.2x', tag: 'Fundo de Funil' }
                    ].map((camp, idx) => (
                      <div key={idx} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                         <div>
                            <div className="flex items-center gap-2 mb-1">
                               <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                               <h5 className="font-semibold text-gray-900 text-sm">{camp.name}</h5>
                               <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase rounded">{camp.tag}</span>
                            </div>
                            <p className="text-xs text-gray-500 ml-4">Investimento: {camp.spend}</p>
                         </div>
                         <div className="flex gap-6 ml-4 md:ml-0">
                            <div className="text-right">
                              <p className="text-[10px] text-gray-500 font-medium uppercase">Leads</p>
                              <p className="font-bold text-gray-900">{camp.leads}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] text-gray-500 font-medium uppercase">CPL</p>
                              <p className="font-bold text-gray-900">{camp.cpl}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] text-gray-500 font-medium uppercase">ROAS</p>
                              <p className="font-bold text-emerald-600">{camp.roas}</p>
                            </div>
                         </div>
                      </div>
                    ))}
                 </div>
               </div>

               {/* Ranking de Criativos */}
               <div className="mt-8">
                 <h4 className="text-sm font-bold text-gray-900 mb-4">Top Criativos (Formato Padrão)</h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { type: 'Criativo de Dor', text: 'Você perde clientes por demorar a responder?', ctr: '3.2%', leads: 85 },
                      { type: 'Criativo de Desejo', text: 'Agenda lotada todos os dias com este método.', ctr: '2.8%', leads: 64 },
                      { type: 'Criativo de Oferta', text: 'Últimas 5 vagas para consultoria gratuita.', ctr: '4.1%', leads: 94 }
                    ].map((cr, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4 flex flex-col justify-between">
                         <div>
                           <span className="text-xs font-bold text-indigo-600 mb-2 block">{cr.type}</span>
                           <p className="text-sm text-gray-700 italic mb-4">"{cr.text}"</p>
                         </div>
                         <div className="flex justify-between items-end border-t border-gray-100 pt-3 mt-2">
                           <div>
                             <p className="text-[10px] text-gray-500 uppercase">CTR</p>
                             <p className="font-bold text-gray-900">{cr.ctr}</p>
                           </div>
                           <div className="text-right">
                             <p className="text-[10px] text-gray-500 uppercase">Leads</p>
                             <p className="font-bold text-gray-900">{cr.leads}</p>
                           </div>
                         </div>
                      </div>
                    ))}
                 </div>
               </div>
             </div>
           </div>
         )}

         {activeTab === 'funil' && (
           <div className="space-y-6 animate-in fade-in">
             <div className="flex items-center justify-between mb-4">
               <div>
                  <h3 className="text-lg font-bold text-gray-900">Funil & Atendimento</h3>
                  <p className="text-sm text-gray-500">Pipeline padronizado do clique até o fechamento/agendamento.</p>
               </div>
             </div>

             <div className="flex flex-col lg:flex-row gap-8">
                {/* Funil Visual */}
                <div className="flex-1 space-y-2">
                  {[
                    { step: 'Cliques no Anúncio', value: '4.520', rate: '100%', color: 'bg-blue-100 text-blue-800' },
                    { step: 'Chegaram na Landing/WA', value: '3.840', rate: '84%', color: 'bg-indigo-100 text-indigo-800' },
                    { step: 'Leads Capturados / Iniciaram Conversa', value: '243', rate: '6.3%', color: 'bg-violet-100 text-violet-800' },
                    { step: 'Qualificados pela IA', value: '180', rate: '74%', color: 'bg-fuchsia-100 text-fuchsia-800' },
                    { step: 'Agendamentos/Vendas', value: '45', rate: '25%', color: 'bg-emerald-100 text-emerald-800' },
                  ].map((stage, idx, arr) => (
                    <div key={idx} className="relative">
                       <div className="flex justify-between items-center bg-gray-50 border border-gray-100 p-4 rounded-xl z-10 relative">
                         <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${stage.color}`}>
                              {idx + 1}
                            </div>
                            <div>
                               <h5 className="font-bold text-gray-900 text-sm">{stage.step}</h5>
                               {idx > 0 && (
                                 <p className="text-xs text-gray-500">Conversão da etapa anterior: <span className="font-semibold text-gray-700">{stage.rate}</span></p>
                               )}
                            </div>
                         </div>
                         <div className="text-xl font-black text-gray-900">{stage.value}</div>
                       </div>
                       {idx < arr.length - 1 && (
                         <div className="absolute left-9 top-10 w-0.5 h-6 bg-gray-200 z-0"></div>
                       )}
                    </div>
                  ))}
                </div>

                {/* Métricas do Atendimento */}
                <div className="w-full lg:w-80 space-y-4">
                   <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><MessageSquare size={16} className="text-gray-400"/> IA vs Humano</h4>
                      <div className="space-y-4">
                         <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-500">Atendimentos Resolvidos (IA)</span>
                              <span className="font-bold text-gray-900">68%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="bg-indigo-500 h-2 rounded-full" style={{ width: '68%' }}></div>
                            </div>
                         </div>
                         <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-500">Transferidos para Humano</span>
                              <span className="font-bold text-gray-900">32%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="bg-orange-400 h-2 rounded-full" style={{ width: '32%' }}></div>
                            </div>
                         </div>
                      </div>
                   </div>

                   <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                      <h4 className="text-sm font-bold text-gray-900 mb-2">Tempo Médio de Resposta</h4>
                      <div className="flex items-end gap-2">
                        <span className="text-3xl font-black text-gray-900">1.2</span>
                        <span className="text-sm text-gray-500 font-medium mb-1">minutos</span>
                      </div>
                      <p className="text-xs text-emerald-600 mt-2 font-medium flex items-center gap-1">
                        <TrendingUp size={12} /> 45% mais rápido que mês passado
                      </p>
                   </div>
                </div>
             </div>
           </div>
         )}

         {activeTab === 'exportacao' && (
           <div className="space-y-6 animate-in fade-in flex flex-col items-center justify-center py-12">
             <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4">
               <Download size={32} />
             </div>
             <div className="text-center max-w-md">
               <h3 className="text-xl font-bold text-gray-900 mb-2">Exportar Relatório Mensal</h3>
               <p className="text-sm text-gray-500 mb-8">
                 Baixe um relatório completo em PDF padronizado com a sua marca para apresentar os resultados da operação.
               </p>
             </div>
             
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
                <button className="flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors">
                  <Download size={18} /> Exportar como PDF
                </button>
                <button className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-3 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors">
                  <Download size={18} /> Exportar como CSV
                </button>
             </div>

             <div className="mt-8 border border-gray-100 bg-gray-50 rounded-xl p-4 w-full max-w-lg flex items-start gap-3">
               <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
               <p className="text-xs text-gray-600">
                 Os relatórios já incluem automaticamente os metadados do cliente, faturamento da equipe, desempenho das campanhas Meta/Google e métricas do funil de vendas. Não é necessário preenchimento manual.
               </p>
             </div>
           </div>
         )}
      </div>
    </div>
  );
};
